import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Button } from '../../components/common'
import { Locale } from '../../types'
import api from '../../services/api'

/**
 * Pagina gestione locali admin
 */
export default function AdminLocali() {
  const [locali, setLocali] = useState<Locale[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchLocali()
  }, [])

  const fetchLocali = async () => {
    try {
      // L'API admin restituisce un array diretto
      const data = await api.get<Locale[]>('/admin/locali')
      setLocali(data ?? [])
    } catch (error) {
      console.error('Errore caricamento locali:', error)
    } finally {
      setLoading(false)
    }
  }

  const toggleAttivo = async (locale: Locale) => {
    try {
      await api.patch(`/admin/locali/${locale.id}`, { attivo: !locale.attivo })
      setLocali(locali.map(l =>
        l.id === locale.id ? { ...l, attivo: !l.attivo } : l
      ))
    } catch (error) {
      console.error('Errore aggiornamento locale:', error)
    }
  }

  const toggleInEvidenza = async (locale: Locale) => {
    try {
      await api.patch(`/admin/locali/${locale.id}`, { inEvidenza: !locale.inEvidenza })
      setLocali(locali.map(l =>
        l.id === locale.id ? { ...l, inEvidenza: !l.inEvidenza } : l
      ))
    } catch (error) {
      console.error('Errore aggiornamento locale:', error)
    }
  }

  const deleteLocale = async (id: number) => {
    if (!confirm('Sei sicuro di voler eliminare questo locale?')) return

    try {
      await api.delete(`/admin/locali/${id}`)
      setLocali(locali.filter(l => l.id !== id))
    } catch (error) {
      console.error('Errore eliminazione locale:', error)
    }
  }

  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map(i => (
          <div key={i} className="bg-night-light border border-gray-800 rounded-xl p-4 animate-pulse">
            <div className="h-6 w-48 bg-gray-700 rounded mb-2"></div>
            <div className="h-4 w-32 bg-gray-700 rounded"></div>
          </div>
        ))}
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <p className="text-gray-400">{locali?.length ?? 0} locali totali</p>
        <Link to="/admin/locali/nuovo">
          <Button variant="primary">
            <span className="flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Nuovo Locale
            </span>
          </Button>
        </Link>
      </div>

      {/* Lista locali */}
      <div className="bg-night-light border border-gray-800 rounded-xl overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-800/50">
            <tr>
              <th className="text-left px-6 py-4 text-sm font-medium text-gray-400">Nome</th>
              <th className="text-left px-6 py-4 text-sm font-medium text-gray-400">Tipo</th>
              <th className="text-left px-6 py-4 text-sm font-medium text-gray-400">Comune</th>
              <th className="text-center px-6 py-4 text-sm font-medium text-gray-400">Attivo</th>
              <th className="text-center px-6 py-4 text-sm font-medium text-gray-400">In Evidenza</th>
              <th className="text-right px-6 py-4 text-sm font-medium text-gray-400">Azioni</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-800">
            {(locali || []).map(locale => (
              <tr key={locale.id} className="hover:bg-gray-800/30">
                <td className="px-6 py-4">
                  <Link
                    to={`/admin/locali/${locale.id}`}
                    className="text-white hover:text-gold transition-colors"
                  >
                    {locale.nome}
                  </Link>
                </td>
                <td className="px-6 py-4">
                  <span className="text-sm text-gray-400 capitalize">{locale.tipo}</span>
                </td>
                <td className="px-6 py-4">
                  <span className="text-sm text-gray-400">{locale.comune}, {locale.provincia}</span>
                </td>
                <td className="px-6 py-4 text-center">
                  <button
                    onClick={() => toggleAttivo(locale)}
                    className={`w-10 h-6 rounded-full transition-colors relative ${
                      locale.attivo ? 'bg-green-500' : 'bg-gray-600'
                    }`}
                  >
                    <span className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                      locale.attivo ? 'left-5' : 'left-1'
                    }`} />
                  </button>
                </td>
                <td className="px-6 py-4 text-center">
                  <button
                    onClick={() => toggleInEvidenza(locale)}
                    className={`w-10 h-6 rounded-full transition-colors relative ${
                      locale.inEvidenza ? 'bg-gold' : 'bg-gray-600'
                    }`}
                  >
                    <span className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                      locale.inEvidenza ? 'left-5' : 'left-1'
                    }`} />
                  </button>
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center justify-end gap-2">
                    <Link
                      to={`/locale/${locale.slug}`}
                      target="_blank"
                      className="p-2 text-gray-400 hover:text-white transition-colors"
                      title="Vedi sul sito"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    </Link>
                    <Link
                      to={`/admin/locali/${locale.id}`}
                      className="p-2 text-gray-400 hover:text-gold transition-colors"
                      title="Modifica"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </Link>
                    <button
                      onClick={() => deleteLocale(locale.id)}
                      className="p-2 text-gray-400 hover:text-red-400 transition-colors"
                      title="Elimina"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
